// components/Header.tsx
import React, { useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import StyledText from '../StyledComponents/StyledText';
import FitnessLogo from '../../assets/icons/FitnessLogo';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';
import { TempSettings, MenuItem } from '../../types/types';

const HeaderContainer = styled.header<{
  $isHovered?: boolean;
  theme: Theme;
  $backgroundColor?: string;
  $layout?: 'left' | 'right' | 'center';
}>`
  background: ${({ $backgroundColor }) => $backgroundColor || 'white'};
  padding: 20px 30px;
  display: flex;
  justify-content: ${({ $layout }) => $layout === 'center' ? 'center' : 'space-between'};
  align-items: center;
  flex-direction: ${({ $layout }) => ($layout === 'center' ? 'column' : $layout === 'right' ? 'row-reverse' : 'row')};
  gap: ${({ $layout }) => ($layout === 'center' ? '12px' : '0')};
  box-shadow: 0px 4px 40px 0px #0000001A;
  border-radius: 10px 10px 0px 0px;
  position: relative;
  transition: all 0.3s;
  border: ${props => props.$isHovered ? `2px solid ${props.theme.CorporateTheme.colors.blueberry}` : '2px solid transparent'};
  overflow: visible;
  
  &:hover {
    box-shadow: 0px 4px 40px 0px rgba(108, 99, 255, 0.2);
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #212529;
  font-size: 18px;
  
  &::before {
    content: '✨';
    font-size: 24px;
  }
`;

const Navigation = styled.nav<{ $textColor?: string; $layout?: 'left' | 'right' | 'center'; $primaryFont?: string }>`
  display: flex;
  gap: 30px;
  justify-content: ${({ $layout }) => ($layout === 'center' ? 'center' : 'flex-start')};
  width: ${({ $layout }) => ($layout === 'center' ? '100%' : 'auto')};
  position: relative;
  z-index: 100;
  overflow: visible;
  
  a {
    color: ${({ $textColor }) => $textColor || '#495057'};
    text-decoration: none;
    font-size: 17px;
    font-weight: 600;
    transition: color 0.2s;
    font-family: ${({ $primaryFont }) => $primaryFont || 'Raleway, sans-serif'};
  }
`;

const NavItem = styled.div`
  position: relative;
  display: inline-block;
  overflow: visible;
`;

const DropdownMenu = styled.div<{ 
  $textColor?: string; 
  $backgroundColor?: string;
  $positionLeft?: boolean;
  $positionTop?: boolean;
}>`
  position: absolute;
  top: ${({ $positionTop }) => $positionTop ? 'auto' : 'calc(100% + 8px)'};
  bottom: ${({ $positionTop }) => $positionTop ? 'calc(100% + 8px)' : 'auto'};
  left: ${({ $positionLeft }) => $positionLeft ? 'auto' : '0'};
  right: ${({ $positionLeft }) => $positionLeft ? '0' : 'auto'};
  background: ${({ $backgroundColor }) => $backgroundColor || 'white'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 200px;
  max-width: 300px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 99999;
  pointer-events: none;

  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  a {
    display: block;
    padding: 10px 20px;
    color: ${({ $textColor }) => $textColor || '#495057'};
    text-decoration: none;
    font-size: 15px;
    font-weight: 500;
    transition: background 0.2s;
    white-space: nowrap;
    
    &:hover {
      background: #f8f9fa;
    }
  }
`;

const SaveDraftButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #212529;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #000;
  }
`;

const EditToolbar = styled.div`
  position: absolute;
  top: -30px;
  right: 0px;
  display: flex;
  z-index: 999999;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  
  ${HeaderContainer}:hover & {
    opacity: 1;
    pointer-events: all;
  }
`;

interface HeaderProps {
  onOpenSettings?: () => void;
  tempSettings?: TempSettings;
  defaultSettings?: TempSettings;
  pendingChanges?: Map<string, TempSettings>;
  isEditing?: boolean;
  menuItems?: MenuItem[];
  primaryFont?: string;
}

const Header: React.FC<HeaderProps> = ({ onOpenSettings, tempSettings = {}, defaultSettings = {}, pendingChanges, isEditing, menuItems = [], primaryFont }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownPositions, setDropdownPositions] = useState<Record<string, { positionLeft: boolean; positionTop: boolean }>>({});
  const navItemRefs = useRef<Record<string, HTMLDivElement>>({});

  const calculateDropdownPosition = (itemId: string, navItemElement: HTMLDivElement) => {
    const dropdown = navItemElement.querySelector('[data-dropdown]') as HTMLElement;
    if (!dropdown) return;

    const navRect = navItemElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get dropdown dimensions (approximate, will be actual on render)
    const dropdownWidth = 300;
    const dropdownHeight = 200;
    const spacing = 8;

    // Check if there's enough space on the right
    const spaceOnRight = viewportWidth - (navRect.right + spacing);
    const positionLeft = spaceOnRight < dropdownWidth;

    // Check if there's enough space at the bottom
    const spaceAtBottom = viewportHeight - (navRect.bottom + spacing);
    const positionTop = spaceAtBottom < dropdownHeight;

    setDropdownPositions(prev => ({
      ...prev,
      [itemId]: { positionLeft, positionTop }
    }));
  };
  
  const appliedSettings = useMemo(() => {
    const headerKey = 'page-header';
    const pendingSettings = pendingChanges?.get(headerKey);
    const baseSettings = pendingSettings ? { ...pendingSettings } : {};
    if (baseSettings._meta) {
      delete baseSettings._meta;
    }
    return {
      ...defaultSettings,
      ...baseSettings,
      ...(isEditing ? tempSettings : {})
    };
  }, [defaultSettings, isEditing, pendingChanges, tempSettings]);
  

  const rawHeaderAlignment = appliedSettings.headerAlignment || 'left-right';
  const headerAlignment = (
    rawHeaderAlignment === 'left-right'
      ? 'left'
      : rawHeaderAlignment === 'right-left'
        ? 'right'
        : rawHeaderAlignment === 'center-stacked'
          ? 'center'
          : rawHeaderAlignment
  ) as 'left' | 'right' | 'center';
  const headerTextColor = appliedSettings.textColor || theme.CorporateTheme.colors.headerColor;
  const headerBackgroundColor = appliedSettings.backgroundColor || 'white';
  
  return (
    <HeaderContainer 
      theme={theme}
      $isHovered={isHovered}
      $layout={headerAlignment}
      $backgroundColor={headerBackgroundColor}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <EditToolbar>
        <div
          style={{ 
            background: theme.CorporateTheme.colors.count, 
            padding: '6px 12px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenSettings?.();
            setIsHovered(false);
          }}
        >
          <StyledText fontSize='14px' fontWeight={600} textColor={theme.CreateFormTheme.colors.background}>
            Edit Header
          </StyledText>
        </div>
      </EditToolbar>

      <div className='d-flex align-items-center gap-2'>
        <div>
          {appliedSettings.websiteLogo ? (
            <img 
              src={appliedSettings.websiteLogo} 
              alt="Website Logo" 
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }} 
            />
          ) : (
            null
          )}
        </div>
      </div>
      <Navigation $textColor={headerTextColor} $layout={headerAlignment} $primaryFont={primaryFont}>
        {menuItems.length > 0 ? (
          menuItems.map((item) => (
            <NavItem 
              key={item.id}
              ref={(el) => {
                if (el) navItemRefs.current[String(item.id)] = el;
              }}
              onMouseEnter={() => calculateDropdownPosition(String(item.id), navItemRefs.current[String(item.id)])}
            >
              <a
                href={item.page.url}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {item.page.name}
              </a>
              {item.children && item.children.length > 0 && (
                <DropdownMenu 
                  data-dropdown
                  $textColor={headerTextColor} 
                  $backgroundColor={headerBackgroundColor}
                  $positionLeft={dropdownPositions[String(item.id)]?.positionLeft ?? false}
                  $positionTop={dropdownPositions[String(item.id)]?.positionTop ?? false}
                >
                  {item.children.map((child) => (
                    <a
                      key={child.id}
                      href={child.page.url}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {child.page.name}
                    </a>
                  ))}
                </DropdownMenu>
              )}
            </NavItem>
          ))
        ) : (
          // Fallback to default links if no menu items
          <>
            <a href="#home" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Home</a>
            <a href="#about" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>About Us</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Contact Us</a>
          </>
        )}
      </Navigation>
    </HeaderContainer>
  );
};

export default Header;